import type { LooseAutoComplete } from '@skyslope/utils';
import cs from 'classnames';
import { twMerge } from 'tailwind-merge';
import type { IBlockState } from '../store/reducers/blocks';
import type { IGroupState } from '../store/reducers/groups';
import type { IBlock, IGroup, ISignatureSettings } from '../store/types';
import { BlockStatus, type PostWindowMessageStatus } from './constants';

const TABBABLE_BLOCK_TYPES = ['Signature', 'Initials', 'Text', 'Checkbox'];

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const sortBlocksByPageOrder = (blocks: IBlock[]) => {
  const result = blocks.sort((a, b) => {
    if (a.y === b.y) return a.x - b.x;
    return a.y - b.y;
  });
  return result;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const buildSortedBlocksArray = (blocks: IBlockState) => {
  let sortedBlockIds: IBlock[] = [];
  Object.keys(blocks).forEach((docId) => {
    Object.keys(blocks[docId]).forEach((pageNumber) => {
      let blockArrayByPage: IBlock[] = [];
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      Object.keys(blocks[docId][pageNumber]).forEach((blockId) => {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        const currentBlock: IBlock = blocks[docId][pageNumber][blockId];
        if (TABBABLE_BLOCK_TYPES.includes(currentBlock.blockType)) {
          blockArrayByPage.push(currentBlock);
        }
      });
      const temp = sortBlocksByPageOrder(blockArrayByPage);
      sortedBlockIds = [...sortedBlockIds, ...temp];
    });
  });
  return sortedBlockIds;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const areGroupRulesValid = (block: IBlock, group: IGroup, blocks: IBlockState | IBlock[]) => {
  const { documentId, pageNumber } = block;
  const validationValue = group.validation.value;
  const blocksInGroup = Array.isArray(blocks)
    ? blocks
    : group.blockIds.map((blockId) => {
        return blocks[documentId][pageNumber][blockId];
      });
  let currentValidationValue = 0;
  blocksInGroup.forEach((currBlock) => {
    const subBlock = currBlock.blockId === block.blockId ? block : currBlock;
    if (subBlock.value) {
      currentValidationValue += 1;
    }
  });
  if (currentValidationValue >= validationValue) {
    return true;
  }
  return false;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getNextBlockAfterGroup = (
  sortedBlocks: IBlock[],
  nextIndices: number[],
  completedPassThrough: boolean,
  groups: IGroupState,
  blocks: IBlockState
) => {
  const { pageNumber, documentId, blockId } = sortedBlocks[nextIndices[0]];
  const startingBlock = blocks[documentId][pageNumber][blockId];
  for (let i = 0; i < nextIndices.length; i++) {
    const { pageNumber, documentId, blockId } = sortedBlocks[nextIndices[i]];
    const currentBlock = blocks[documentId][pageNumber][blockId];
    if (!currentBlock.groupId) {
      if (completedPassThrough && currentBlock.required && currentBlock.status !== BlockStatus.COMPLETE) {
        return nextIndices[i];
      } else if (!completedPassThrough) {
        return nextIndices[i];
      } else if (completedPassThrough && currentBlock.status === BlockStatus.COMPLETE) {
        continue;
      }
    } else if (currentBlock.groupId && currentBlock.groupId !== startingBlock.groupId) {
      const currentGroup = groups[currentBlock.documentId][currentBlock.pageNumber][currentBlock.groupId];
      if (currentGroup.validation.rule !== 'lte' && !areGroupRulesValid(currentBlock, currentGroup, blocks)) {
        return nextIndices[i];
      }
      if (currentGroup.validation.rule === 'lte' && !completedPassThrough) {
        return nextIndices[i];
      }
    }
  }
  return null;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getNextBlockInOrder = ({
  currentIndex,
  blocks,
  completedPassThrough,
  sortedBlocks,
  groups,
  completedIndex,
}: {
  currentIndex: number | null;
  blocks: IBlockState;
  completedPassThrough: boolean;
  sortedBlocks: IBlock[];
  groups?: IGroupState;
  completedIndex?: number;
}) => {
  const startingIndex = currentIndex !== null ? currentIndex + 1 : 0;
  const nextIndices = Array.from(Array(sortedBlocks.length).keys()).map(
    (i) => (i + startingIndex) % sortedBlocks.length
  );
  const block = sortedBlocks[nextIndices[nextIndices.length - 1]];
  if (
    block.groupId &&
    areGroupRulesValid(
      sortedBlocks[nextIndices[nextIndices.length - 1]],
      groups![block.documentId][block.pageNumber][block.groupId],
      blocks
    )
  ) {
    const nextBlock = getNextBlockAfterGroup(sortedBlocks, nextIndices, completedPassThrough, groups!, blocks);
    return nextBlock;
  }
  for (let i = 0; i < nextIndices.length; i++) {
    const { pageNumber, documentId, blockId } = sortedBlocks[nextIndices[i]];
    const currentBlock = blocks[documentId][pageNumber][blockId];
    const startingBlock =
      currentIndex === null ? sortedBlocks[nextIndices[0]] : sortedBlocks[nextIndices[sortedBlocks.length - 1]];
    if (
      currentBlock.groupId &&
      !areGroupRulesValid(
        currentBlock,
        groups![currentBlock.documentId][currentBlock.pageNumber][currentBlock.groupId],
        blocks
      )
    ) {
      const currentGroup = groups![currentBlock.documentId][currentBlock.pageNumber][currentBlock.groupId];
      if (startingBlock.groupId === currentBlock.groupId && i === 0) {
        continue;
      }
      if (currentGroup.validation.rule === 'lte' && !completedPassThrough) {
        return nextIndices[i];
      }
      if (currentGroup.validation.rule === 'lte' && completedPassThrough) {
        continue;
      }
      return nextIndices[i];
    }
    if (
      (!completedPassThrough || currentBlock.required) &&
      currentBlock.status !== BlockStatus.COMPLETE &&
      TABBABLE_BLOCK_TYPES.includes(currentBlock.blockType) &&
      currentBlock.status === 'Pending'
    ) {
      if (completedIndex && currentBlock.blockId === sortedBlocks[completedIndex].blockId) {
        continue;
      }
      return nextIndices[i];
    }
  }
  return null;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const findBlockById = (block: IBlock, blocks: IBlock[]) =>
  blocks.find((currentBlock) => currentBlock.blockId === block.blockId);

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getBlockByGroupId = (block: IBlock, blocks: IBlock[]) =>
  blocks.find((currentBlock) => block.groupId === currentBlock.groupId);

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getGroupValidationCount = (
  documentId: string,
  pageIndex: number,
  group: IGroup,
  blocks: IBlockState | IBlock[]
) => {
  const blocksInGroup = Array.isArray(blocks)
    ? blocks
    : group.blockIds.map((blockId) => {
        return blocks[documentId][pageIndex][blockId];
      });
  let currentValidationValue = 0;
  blocksInGroup.forEach((currBlock) => {
    if (currBlock.value) {
      currentValidationValue += 1;
    }
  });
  return currentValidationValue;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const handleFontSizing = (
  height: number,
  width: number,
  zoom: number,
  initialFontSize: number,
  initialValue: string
) => {
  const defaultFontSize = Math.min(height - 1, initialFontSize || 24, 24);
  const value = initialValue || '';
  const newFontSize = handleCalculateFontSize(value, defaultFontSize, height, width, zoom);
  return newFontSize;
};

const hiddenDiv = document.createElement('div');
const style: { [key: string]: string | number } = {
  fontFamily: 'courier',
  overflow: 'hidden',
  boxSizing: 'content-box',
  wordBreak: 'break-word',
  textAlign: 'left',
  lineHeight: 1,
  position: 'fixed',
  whiteSpace: 'break-spaces',
  visibility: 'hidden',
};
for (const key in style) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  hiddenDiv.style[key] = style[key];
}
if (document.body) {
  document.body.appendChild(hiddenDiv);
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const shrink = (fontSize: number, height: number, zoom: number): number => {
  let newFontSize = Math.min(fontSize, height) - 1;
  while (newFontSize > 0) {
    hiddenDiv.style.fontSize = `${newFontSize * zoom}px`;
    if (hiddenDiv.clientHeight < height * zoom) {
      return newFontSize;
    }
    newFontSize--;
  }
  return 0;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const grow = (fontSize: number, height: number, zoom: number): number => {
  const maxFontSize = Math.min(height - 1, 25);
  let newFontSize = Math.min(fontSize + 1, maxFontSize);
  while (newFontSize <= maxFontSize) {
    hiddenDiv.style.fontSize = `${newFontSize * zoom}px`;
    if (hiddenDiv.clientHeight < height * zoom) {
      newFontSize++;
    } else {
      return newFontSize - 1;
    }
  }
  return newFontSize - 1;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const handleCalculateFontSize = (
  value: string,
  fontSize: number,
  height: number,
  width: number,
  zoom: number
): number => {
  hiddenDiv.style.width = `${width * zoom - 8}px`;
  hiddenDiv.textContent = value;
  hiddenDiv.style.fontSize = `${fontSize * zoom}px`;
  if (hiddenDiv.clientHeight >= height * zoom || fontSize >= height) {
    return shrink(fontSize, height, zoom);
  }
  return grow(fontSize, height, zoom);
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const checkIfSignerHasSettingsSaved = (settings?: ISignatureSettings) => {
  if (!settings) return false;
  return (
    (settings?.signatureType === 'text' && settings?.signatureFont) ||
    (settings?.signatureType === 'image' &&
      ((settings?.signatureValue?.length ?? 0) > 10 ||
        (settings?.initialsValue?.length ?? 0) > 10 ||
        (settings?.initialsImageBase64?.length ?? 0) > 10 ||
        (settings?.signatureImageBase64?.length ?? 0) > 10))
  );
};

/**
 * THIS IS COPIED FROM SKYSLOPE-UTILS, WE SHOULD JUST USE THAT LIBRARY DIRECTLY
 *
 * Clamps a value between an upper and a lower bound, enforcing
 * both a minimum and a maximum at the same time.
 * @param num The value to be clamped.
 * @param args An object specifying the min & max values used
 * as bounds.
 * @returns The clamped value.
 */
export function clamp(num: number, args: { min: number; max: number }) {
  const { min, max } = args;
  return Math.min(Math.max(num, min), max);
}

export function cn(...args: any[]) {
  return twMerge(cs(...args));
}

/**
 * Utility function to post a message to the parent window.
 * This function uses `window.postMessage` to send structured messages.
 *
 * @param message - The message object containing the event name and optional payload.
 */
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const postWindowMessage = (body: {
  header_text: LooseAutoComplete<'Signature'>;
  back_button_type: LooseAutoComplete<'<'>;
  should_back_button_exit: boolean;
  button_text: LooseAutoComplete<'Send'>;
  digi_mobile_status?: PostWindowMessageStatus;
  metadata?: {
    digisignEnvelopeId?: string;
    envelope_status?: LooseAutoComplete<'sent' | 'sentWithSignNow'>;
    totalSignatureBlocks?: string;
    currentBlocksSigned?: string;
  };
}) => {
  window.webkit?.messageHandlers?.mobileAppListener?.postMessage(JSON.stringify(body));
};

export function isSkySlopeMobileApp(): boolean {
  const userAgent = typeof window.navigator === 'undefined' ? '' : navigator.userAgent;
  return Boolean(userAgent.match(/MobileApp.*SkySlope/i));
}
